/**
 * i18n/workspace.ts — Vietnamese + English strings cho Workspace UI.
 *
 * CR-WS-028 | TASK-UI-WS028-011
 *
 * Namespace: 'workspace'
 * 50+ keys: switcher, type, intent, classification, task, quota, offline, degradation, empty, layout, approval.
 * Interpolations: {{name}}, {{count}}, {{percent}}.
 */

export const workspaceNamespace = {
  vi: {
    // workspace.switcher.* (4 keys)
    "workspace.switcher.current": "Workspace hiện tại: {{name}}",
    "workspace.switcher.label": "Chuyển workspace",
    "workspace.switcher.search_placeholder": "Tìm kiếm workspace...",
    "workspace.switcher.create": "Tạo workspace mới",

    // workspace.type.* (6 keys)
    "workspace.type.personal": "Cá nhân",
    "workspace.type.team": "Đội ngũ",
    "workspace.type.department": "Phòng ban",
    "workspace.type.customer": "Khách hàng",
    "workspace.type.partner": "Đối tác",
    "workspace.type.project": "Dự án",

    // intent.* (7 keys)
    "intent.coding": "Lập trình",
    "intent.research": "Nghiên cứu",
    "intent.writing": "Soạn thảo văn bản",
    "intent.review": "Đánh giá tài liệu",
    "intent.general": "Chung",
    "intent.detected": "Ý định phát hiện: {{intent}}",
    "intent.badge": "Ý định AI",

    // classification.* (5 keys)
    "classification.public": "CÔNG KHAI",
    "classification.internal": "NỘI BỘ",
    "classification.restricted": "HẠN CHẾ",
    "classification.confidential": "BÍ MẬT",
    "classification.highly_restricted": "TUYỆT MẬT",

    // task.step.* & task.* (7 keys)
    "task.step.pending": "Đang chờ",
    "task.step.running": "Đang thực hiện",
    "task.step.completed": "Đã hoàn thành",
    "task.step.failed": "Thất bại",
    "task.pause": "Tạm dừng tác vụ",
    "task.stop": "Dừng tác vụ",
    "task.active_count": "{{count}} tác vụ đang chạy",

    // quota.* (2 keys)
    "quota.warning": "Cảnh báo: Bạn đã dùng {{percent}}% hạn mức token hôm nay",
    "quota.exceeded": "Đã vượt quá hạn mức token hàng ngày của workspace",

    // offline.* (2 keys)
    "offline.banner": "Đang mất kết nối mạng. Các thay đổi sẽ được lưu cục bộ.",
    "offline.pending_changes": "{{count}} thay đổi đang chờ đồng bộ",

    // degradation.* (2 keys)
    "degradation.banner": "Dịch vụ đang trong chế độ giảm tải để duy trì tính sẵn sàng.",
    "degradation.action": "Xem trạng thái hệ thống",

    // empty.* (9 keys)
    "empty.tasks_title": "Chưa có tác vụ nào",
    "empty.tasks_desc": "Tạo tác vụ đầu tiên để AI hỗ trợ tự động hóa luồng làm việc.",
    "empty.tasks_action": "Tạo tác vụ mới",
    "empty.docs_title": "Chưa có tài liệu",
    "empty.docs_desc": "Tải lên hoặc thêm tài liệu vào ngữ cảnh của workspace.",
    "empty.docs_action": "Thêm tài liệu",
    "empty.projects_title": "Chưa có dự án",
    "empty.projects_desc": "Tạo dự án mới để phân loại công việc và phân quyền.",
    "empty.projects_action": "Tạo dự án",

    // layout.* (4 keys)
    "layout.sidebar_toggle": "Ẩn/hiện thanh điều hướng bên",
    "layout.agent_panel_toggle": "Mở bảng trợ lý AI",
    "layout.fullscreen": "Toàn màn hình",
    "layout.reset": "Đặt lại bố cục mặc định",

    // approval.* (4 keys)
    "approval.agent_wants": "AI yêu cầu cấp quyền thực thi hành động",
    "approval.allow_once": "Cho phép một lần",
    "approval.always_allow": "Luôn cho phép trong phiên này",
    "approval.deny": "Từ chối thực thi",
  },
  en: {
    // workspace.switcher.* (4 keys)
    "workspace.switcher.current": "Current workspace: {{name}}",
    "workspace.switcher.label": "Switch workspace",
    "workspace.switcher.search_placeholder": "Search workspaces...",
    "workspace.switcher.create": "New workspace",

    // workspace.type.* (6 keys)
    "workspace.type.personal": "Personal",
    "workspace.type.team": "Team",
    "workspace.type.department": "Department",
    "workspace.type.customer": "Customer",
    "workspace.type.partner": "Partner",
    "workspace.type.project": "Project",

    // intent.* (7 keys)
    "intent.coding": "Coding",
    "intent.research": "Research",
    "intent.writing": "Writing",
    "intent.review": "Review",
    "intent.general": "General",
    "intent.detected": "Detected intent: {{intent}}",
    "intent.badge": "AI Intent",

    // classification.* (5 keys)
    "classification.public": "PUBLIC",
    "classification.internal": "INTERNAL",
    "classification.restricted": "RESTRICTED",
    "classification.confidential": "CONFIDENTIAL",
    "classification.highly_restricted": "HIGHLY RESTRICTED",

    // task.step.* & task.* (7 keys)
    "task.step.pending": "Pending",
    "task.step.running": "Running",
    "task.step.completed": "Completed",
    "task.step.failed": "Failed",
    "task.pause": "Pause task",
    "task.stop": "Stop task",
    "task.active_count": "{{count}} active tasks",

    // quota.* (2 keys)
    "quota.warning": "Warning: You have used {{percent}}% of your daily token limit",
    "quota.exceeded": "Daily workspace token limit exceeded",

    // offline.* (2 keys)
    "offline.banner": "You are offline. Changes are saved locally and will sync once reconnected.",
    "offline.pending_changes": "{{count}} changes pending sync",

    // degradation.* (2 keys)
    "degradation.banner": "Service is currently operating in degraded mode to maintain availability.",
    "degradation.action": "View system status",

    // empty.* (9 keys)
    "empty.tasks_title": "No tasks yet",
    "empty.tasks_desc": "Create your first task to automate workflows with AI.",
    "empty.tasks_action": "Create task",
    "empty.docs_title": "No documents yet",
    "empty.docs_desc": "Upload or add documents to your workspace context.",
    "empty.docs_action": "Add document",
    "empty.projects_title": "No projects yet",
    "empty.projects_desc": "Create a project to organize tasks and permissions.",
    "empty.projects_action": "Create project",

    // layout.* (4 keys)
    "layout.sidebar_toggle": "Toggle navigation sidebar",
    "layout.agent_panel_toggle": "Toggle AI agent panel",
    "layout.fullscreen": "Toggle fullscreen",
    "layout.reset": "Reset layout",

    // approval.* (4 keys)
    "approval.agent_wants": "AI is requesting permission to execute an action",
    "approval.allow_once": "Allow once",
    "approval.always_allow": "Always allow in this session",
    "approval.deny": "Deny execution",
  },
};

// Backward-compatibility export
export const workspaceStrings = workspaceNamespace;
